import { useEffect, useCallback } from 'react';

/**
 * Detects whether the user is on a Mac platform.
 */
const isMac = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform);

/**
 * Returns true if the event target is a text input element where
 * keyboard shortcuts should be suppressed.
 */
function isTypingInInput(event) {
  const tag = event.target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true;
  if (event.target.isContentEditable) return true;
  // Also check for role="textbox" (used by some rich editors)
  if (event.target.getAttribute('role') === 'textbox') return true;
  return false;
}

/**
 * Platform-aware modifier key label.
 */
export function getModKey() {
  return isMac() ? '⌘' : 'Ctrl';
}

/**
 * Hook to register a keyboard shortcut.
 *
 * @param {string} key - The key to listen for (e.g. 'k', 's', 'z', '?', 'Escape')
 * @param {Function} handler - The callback to invoke
 * @param {Object} options
 * @param {boolean} options.mod - Require Cmd (Mac) or Ctrl (Win/Linux)
 * @param {boolean} options.shift - Require Shift
 * @param {boolean} options.allowInInput - Fire even when focused on an input (default false)
 * @param {boolean} options.enabled - Whether the shortcut is active (default true)
 * @param {Array} deps - Additional deps for the handler callback
 */
export function useKeyboardShortcut(key, handler, options = {}, deps = []) {
  const {
    mod = false,
    shift = false,
    allowInInput = false,
    enabled = true,
  } = options;

  const stableHandler = useCallback(handler, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      // Skip inputs unless explicitly allowed
      if (!allowInInput && isTypingInInput(e)) return;

      // Check modifier keys
      const modKeyPressed = isMac() ? e.metaKey : e.ctrlKey;
      if (mod && !modKeyPressed) return;
      if (!mod && (e.metaKey || e.ctrlKey)) return;
      if (shift && !e.shiftKey) return;
      if (!shift && e.shiftKey && key !== '?') return; // ? requires shift on most keyboards

      // Match the key (case-insensitive for letters)
      const pressedKey = e.key.toLowerCase();
      const targetKey = key.toLowerCase();

      if (pressedKey !== targetKey) return;

      e.preventDefault();
      stableHandler(e);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [key, mod, shift, allowInInput, enabled, stableHandler]);
}

/**
 * All platform keyboard shortcuts with metadata for the cheat sheet.
 */
export const SHORTCUTS = [
  {
    id: 'search',
    keys: [getModKey(), 'K'],
    label: 'Open search',
    scope: 'Global',
    category: 'Navigation',
  },
  {
    id: 'shortcuts-help',
    keys: ['?'],
    label: 'Show keyboard shortcuts',
    scope: 'Global',
    category: 'Navigation',
  },
  {
    id: 'save',
    keys: [getModKey(), 'S'],
    label: 'Save',
    scope: 'Builders',
    category: 'Editing',
  },
  {
    id: 'undo',
    keys: [getModKey(), 'Z'],
    label: 'Undo',
    scope: 'Page Builder',
    category: 'Editing',
  },
  {
    id: 'redo',
    keys: [getModKey(), 'Shift', 'Z'],
    label: 'Redo',
    scope: 'Page Builder',
    category: 'Editing',
  },
  {
    id: 'escape',
    keys: ['Esc'],
    label: 'Close modal / panel',
    scope: 'Global',
    category: 'Navigation',
  },
  {
    id: 'duplicate',
    keys: [getModKey(), 'D'],
    label: 'Duplicate selection',
    scope: 'Page Builder',
    category: 'Editing',
  },
  {
    id: 'delete',
    keys: [getModKey(), 'Backspace'],
    label: 'Delete selection',
    scope: 'Page Builder',
    category: 'Editing',
  },
  {
    id: 'zoom-in',
    keys: [getModKey(), '+'],
    label: 'Zoom in',
    scope: 'Page Builder',
    category: 'View',
  },
  {
    id: 'zoom-out',
    keys: [getModKey(), '−'],
    label: 'Zoom out',
    scope: 'Page Builder',
    category: 'View',
  },
  {
    id: 'zoom-reset',
    keys: [getModKey(), '0'],
    label: 'Reset zoom',
    scope: 'Page Builder',
    category: 'View',
  },
];

export default useKeyboardShortcut;
